import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useNoshStore } from '../lib/store';
import { useLeftEdgeSwipeDisable } from '../hooks/useLeftEdgeSwipeDisable';
import { Plus, Check, Trash2, ShoppingBag, Search, Receipt, Clock, Edit3, User } from 'lucide-react';
import type { ShoppingList as ShoppingListType, ShoppingListItem } from '../lib/database';
// import { ThemeToggle } from '../components/ui/theme-toggle';

const ShoppingList = () => {
  const { 
    shoppingLists,
    loadShoppingLists,
    addShoppingListItem,
    updateShoppingListItemName,
    toggleShoppingListItem,
    deleteShoppingListItem,
    deleteShoppingList
  } = useNoshStore();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemQuantity, setNewItemQuantity] = useState<number | string>(1);
  const [newItemUnit, setNewItemUnit] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [listToDelete, setListToDelete] = useState<string | null>(null);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Load shopping lists on component mount
  useEffect(() => {
    loadShoppingLists();
  }, [loadShoppingLists]);
  
  // Prevent browser native left edge swipe gesture on mobile
  useLeftEdgeSwipeDisable({
    pageId: 'ShoppingList',
    enabled: true,
    edgeWidth: 50
  });

  // Get all items from all shopping lists and filter by search
  const allItems = shoppingLists.flatMap(list => 
    list.items.map(item => ({
      ...item,
      listTitle: list.title,
      listId: list.id
    }))
  );

  const filteredItems = allItems.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.listTitle.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Separate Manual Items and organize categories with Manual Items first
  const manualItems = filteredItems.filter(item => item.category === 'Manual Items');
  const otherItems = filteredItems.filter(item => item.category !== 'Manual Items');
  const otherCategories = [...new Set(otherItems.map(item => item.category || 'Other'))];
  
  const checkedItems = filteredItems.filter(item => item.isPurchased);
  const uncheckedItems = filteredItems.filter(item => !item.isPurchased);

  const handleToggleItem = async (itemId: string) => {
    await toggleShoppingListItem(itemId);
  };

  const handleDeleteItem = async (itemId: string) => {
    await deleteShoppingListItem(itemId);
  };

  const handleDeleteList = (listId: string) => {
    setListToDelete(listId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteList = async () => {
    if (listToDelete) {
      await deleteShoppingList(listToDelete);
      setListToDelete(null);
    }
    setShowDeleteConfirm(false);
  };

  const cancelDeleteList = () => {
    setListToDelete(null);
    setShowDeleteConfirm(false);
  };

  const handleAddItem = async () => {
    if (newItemName.trim()) {
      const quantity = typeof newItemQuantity === 'string' ? parseFloat(newItemQuantity) || 1 : newItemQuantity;
      await addShoppingListItem({
        name: newItemName.trim(),
        quantity: quantity,
        unit: newItemUnit.trim() || undefined,
        category: 'Manual Items'
      });
      
      // Reset form
      setNewItemName('');
      setNewItemQuantity(1);
      setNewItemUnit('');
      setShowAddItemModal(false);
    }
  };

  const handleUpdateItemName = async (itemId: string, newName: string) => {
    if (newName.trim() && newName.trim() !== '') {
      await updateShoppingListItemName(itemId, newName.trim());
    }
  };

  const formatQuantity = (quantity: number, unit?: string | null) => {
    if (!unit) return quantity.toString();
    return `${quantity} ${unit}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={`max-w-4xl mx-auto ${isMobile ? 'pb-20' : ''}`}>
      {/* Mobile Simple Text Header - Match Inventory Design */}
      {isMobile && (
        <div className="px-4 mb-6 pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-bold text-gray-900 mb-1">Shopping Lists</h1>
              <p className="text-gray-600 text-sm">Your saved shopping lists from meal planning</p>
            </div>
            <Button 
              onClick={() => setShowAddItemModal(true)}
              size="sm"
              className="ml-2 bg-primary text-white hover:bg-primary/90"
            >
              <Plus size={18} className="mr-1 text-white" />
              Add Item
            </Button>
          </div>
        </div>
      )}

      {/* Desktop Header - only show on desktop */}
      {!isMobile && (
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-heading font-bold">Shopping Lists</h1>
          <Button 
            onClick={() => setShowAddItemModal(true)}
            className="bg-primary text-white hover:bg-primary/90"
          >
            <Plus size={18} className="mr-2 text-white" />
            Add Item
          </Button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" size={18} />
          <input
            type="text"
            placeholder="Search items, categories, or lists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>



        {/* Summary Stats */}
        {shoppingLists.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-2 md:p-3 text-center">
              <div className="text-xl md:text-2xl font-bold text-blue-600">{shoppingLists.length}</div>
              <div className="text-xs md:text-sm text-blue-600">Lists</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-2 md:p-3 text-center">
              <div className="text-xl md:text-2xl font-bold text-orange-600">{uncheckedItems.length}</div>
              <div className="text-xs md:text-sm text-orange-600">To Buy</div>
            </div>
            <div className="bg-green-50 rounded-lg p-2 md:p-3 text-center">
              <div className="text-xl md:text-2xl font-bold text-green-600">{checkedItems.length}</div>
              <div className="text-xs md:text-sm text-green-600">Purchased</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-2 md:p-3 text-center">
              <div className="text-xl md:text-2xl font-bold text-purple-600">{manualItems.length + otherCategories.length}</div>
              <div className="text-xs md:text-sm text-purple-600">Categories</div>
            </div>
          </div>
        )}
        
        <div className="space-y-6">
          {/* Items still to buy */}
          <div>
            <h2 className="font-heading font-semibold mb-3 flex items-center gap-2">
              <ShoppingBag size={20} className="text-orange-600" />
              To Buy ({uncheckedItems.length})
            </h2>
            
            {uncheckedItems.length > 0 ? (
              <div className="space-y-3">
                {/* Manual Items Section - Always First */}
                {manualItems.filter(item => !item.isPurchased).length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-purple-600 mb-2 flex items-center gap-1">
                      <User size={14} />
                      Manual Items
                    </h3>
                    <div className="space-y-2">
                      {manualItems.filter(item => !item.isPurchased).map(item => (
                        <EditableShoppingItemComponent 
                          key={item.id} 
                          item={item} 
                          onToggle={() => handleToggleItem(item.id)} 
                          onDelete={() => handleDeleteItem(item.id)} 
                          onUpdateName={(newName) => handleUpdateItemName(item.id, newName)}
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Other Categories */}
                {otherCategories.map(category => {
                  const categoryItems = otherItems.filter(item => 
                    (item.category || 'Other') === category && !item.isPurchased
                  );
                  if (categoryItems.length === 0) return null;
                  
                  return (
                    <div key={category}>
                      <h3 className="text-sm font-medium text-text-secondary mb-2 capitalize">{category}</h3>
                      <div className="space-y-2">
                        {categoryItems.map(item => (
                          <EditableShoppingItemComponent 
                            key={item.id} 
                            item={item} 
                            onToggle={() => handleToggleItem(item.id)} 
                            onDelete={() => handleDeleteItem(item.id)} 
                            onUpdateName={(newName) => handleUpdateItemName(item.id, newName)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 bg-muted rounded-lg">
                <ShoppingBag size={32} className="mx-auto mb-2 text-text-secondary opacity-50" />
                <p className="text-text-muted">No items to buy</p>
                <p className="text-xs text-text-muted mt-1">Generate a shopping list from your meal planner or add items manually</p>
              </div>
            )}
          </div>
          
          {/* Items already purchased */}
          {checkedItems.length > 0 && (
            <div>
              <h2 className="font-heading font-semibold mb-3 flex items-center gap-2">
                <Check size={20} className="text-green-600" />
                Purchased ({checkedItems.length})
              </h2>
              <div className="space-y-2 opacity-70">
                {checkedItems.map(item => (
                  <EditableShoppingItemComponent 
                    key={item.id} 
                    item={item} 
                    onToggle={() => handleToggleItem(item.id)} 
                    onDelete={() => handleDeleteItem(item.id)} 
                    onUpdateName={(newName) => handleUpdateItemName(item.id, newName)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Shopping Lists Management */}
      {shoppingLists.length > 0 && (
        <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
          <h2 className="font-heading font-semibold mb-4 flex items-center gap-2">
            <Clock size={20} className="text-blue-600" />
            Your Shopping Lists
          </h2>
          <div className="space-y-3">
            {shoppingLists.map(list => (
              <div key={list.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{list.title}</h3>
                  <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                    <span>{list.items.length} items</span>
                    <span>•</span>
                    <span>{list.items.filter(item => item.isPurchased).length} purchased</span>
                    <span>•</span>
                    <span>{formatDate(list.createdAt)}</span>
                  </div>
                  {list.notes && (
                    <p className="text-sm text-gray-500 mt-1">{list.notes}</p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteList(list.id)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      



      {/* Add Item Modal */}
      {showAddItemModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowAddItemModal(false)} />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 m-4 max-w-md w-full relative z-10"
          >
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User size={20} className="text-purple-600" />
              Add Manual Item
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Item Name *
                </label>
                <input
                  type="text"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  placeholder="e.g., Organic Bananas"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  autoFocus
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.1"
                    value={newItemQuantity}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setNewItemQuantity('');
                      } else {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue) && numValue > 0) {
                          setNewItemQuantity(numValue);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Ensure valid value when input loses focus
                      if (newItemQuantity === '' || newItemQuantity === 0) {
                        setNewItemQuantity(1);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit (optional)
                  </label>
                  <input
                    type="text"
                    value={newItemUnit}
                    onChange={(e) => setNewItemUnit(e.target.value)}
                    placeholder="e.g., lbs, pieces"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowAddItemModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddItem}
                disabled={!newItemName.trim()}
                className="flex-1"
              >
                Add Item
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "linear" }}
            onClick={cancelDeleteList}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
          />

          {/* Modal Content */}
          <div className="relative z-10 m-4">
            {isMobile ? (
              // Mobile: iOS-styled confirmation
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm mx-auto overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Content */}
                <div className="text-center p-6 pb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Delete Shopping List
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                    Are you sure you want to delete this shopping list and all its items? This action cannot be undone.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="border-t border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-2">
                    <button
                      onClick={cancelDeleteList}
                      className="px-6 py-4 text-blue-600 dark:text-blue-400 font-medium border-r border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDeleteList}
                      className="px-6 py-4 text-red-600 dark:text-red-400 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            ) : (
              // Desktop: Modern web-styled confirmation
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 w-full max-w-md mx-auto overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header with Icon */}
                <div className="text-center p-8 pb-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30 rounded-2xl flex items-center justify-center">
                    <Trash2 size={24} className="text-red-600 dark:text-red-400" />
                  </div>
                  <h3 className="text-xl font-heading font-bold text-gray-900 dark:text-gray-100 mb-3">
                    Delete Shopping List
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    Are you sure you want to delete this shopping list and all its items? This action cannot be undone and all data will be permanently removed.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="px-8 pb-8 flex gap-3">
                  <Button
                    onClick={cancelDeleteList}
                    variant="outline"
                    className="flex-1 h-11 rounded-xl font-medium border-2 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Keep List
                  </Button>
                  <Button
                    onClick={confirmDeleteList}
                    className="flex-1 h-11 rounded-xl font-medium bg-red-600 hover:bg-red-700 text-white border-2 border-red-600 hover:border-red-700"
                  >
                    Delete Forever
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface EditableShoppingItemComponentProps {
  item: ShoppingListItem & { listTitle: string; listId: string };
  onToggle: () => void;
  onDelete: () => void;
  onUpdateName: (newName: string) => void;
}

const EditableShoppingItemComponent = ({ item, onToggle, onDelete, onUpdateName }: EditableShoppingItemComponentProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(item.name);
  const inputRef = useRef<HTMLInputElement>(null);

  const formatQuantity = (quantity: number, unit?: string | null) => {
    if (!unit) return quantity.toString();
    return `${quantity} ${unit}`;
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditName(item.name);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSaveEdit = () => {
    if (editName.trim() && editName.trim() !== item.name) {
      onUpdateName(editName.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditName(item.name);
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-2xl border-2 transition-all duration-200 ease-out ${
        item.isPurchased
          ? 'bg-green-50 border-transparent shadow-md'
          : 'bg-gray-50 border-transparent shadow-sm hover:shadow-md hover:bg-gray-100'
      }`}
    >
      <button
        onClick={onToggle}
        className="w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 touch-manipulation"
      >
        {item.isPurchased ? (
          <>
            <div className="absolute w-5 h-5 bg-white border-white rounded-full shadow-sm" />
            <div className="w-2.5 h-2.5 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full relative z-10" />
          </>
        ) : (
          <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
        )}
      </button>
      
      <div className="flex-1">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleSaveEdit}
            onKeyDown={handleKeyPress}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        ) : (
          <div 
            className={`font-medium cursor-pointer group flex items-center gap-2 ${
              item.isPurchased ? 'line-through text-gray-600' : 'text-gray-900 hover:text-primary'
            }`}
            onClick={handleStartEdit}
          >
            {item.name}
            <Edit3 size={12} className="opacity-0 group-hover:opacity-50 transition-opacity" />
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>{formatQuantity(item.quantity, item.unit)}</span>
          {item.category === 'Manual Items' && (
            <>
              <span>•</span>
              <span className="text-purple-600 text-xs flex items-center gap-1">
                <User size={10} />
                Manual
              </span>
            </>
          )}
        </div>
      </div>
      
      <button
        onClick={onDelete}
        className="w-8 h-8 rounded-full bg-gray-100 hover:bg-red-100 flex items-center justify-center transition-colors group"
      >
        <Trash2 size={14} className="text-gray-400 group-hover:text-red-600" />
      </button>
    </div>
  );
};

export default ShoppingList;